import styled, { keyframes } from 'styled-components';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  33% {
    transform: translateY(-20px) rotate(120deg);
  }
  66% {
    transform: translateY(10px) rotate(240deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

// Main Container
export const LoginContainer = styled.div`
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 10px;
  }
`;

// Background Elements
export const BackgroundOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

export const AnimatedShape = styled.div`
  position: absolute;
  width: 200px;
  height: 200px;
  background: linear-gradient(45deg, rgba(43, 56, 87, 0.08), rgba(197, 51, 123, 0.05));
  border-radius: 50%;
  animation: ${float} 15s ease-in-out infinite;
  animation-delay: ${props => props.delay};
  
  &:nth-child(1) {
    top: 10%;
    left: 10%;
    width: 150px;
    height: 150px;
  }
  
  &:nth-child(2) {
    top: 60%;
    right: 10%;
    width: 100px;
    height: 100px;
  }
  
  &:nth-child(3) {
    bottom: 20%;
    left: 20%;
    width: 120px;
    height: 120px;
  }

  @media (max-width: 768px) {
    &:nth-child(1) {
      width: 100px;
      height: 100px;
    }
    
    &:nth-child(2) {
      width: 80px;
      height: 80px;
    }
    
    &:nth-child(3) {
      width: 90px;
      height: 90px;
    }
  }
`;

// Content Wrapper
export const ContentWrapper = styled.div`
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 400px;
  animation: ${fadeInUp} 0.8s ease-out;
`;

// Login Card
export const LoginCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(43, 56, 87, 0.1);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(43, 56, 87, 0.15);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 25px 50px rgba(43, 56, 87, 0.2);
  }

  @media (max-width: 768px) {
    padding: 30px 20px;
    border-radius: 15px;
  }
`;

// Logo Section
export const LogoSection = styled.div`
  text-align: center;
  margin-bottom: 40px;
  animation: ${fadeInUp} 1s ease-out 0.2s both;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

export const Title = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  color: #2B3857;
  margin: 0 0 10px 0;
  text-shadow: 0 4px 8px rgba(43, 56, 87, 0.2);
  animation: ${pulse} 2s ease-in-out infinite;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

export const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #C5337B;
  margin: 0;
  font-weight: 300;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

// Form
export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  animation: ${fadeInUp} 1s ease-out 0.4s both;
`;

// Input Group
export const InputGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const InputIcon = styled.div`
  position: absolute;
  left: 15px;
  z-index: 2;
  color: rgba(43, 56, 87, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
`;

export const Input = styled.input`
  width: 100%;
  padding: 15px 15px 15px 50px;
  background: rgba(248, 250, 252, 0.8);
  border: 2px solid rgba(43, 56, 87, 0.15);
  border-radius: 12px;
  color: #2B3857;
  font-size: 1rem;
  transition: all 0.3s ease;

  &::placeholder {
    color: rgba(43, 56, 87, 0.5);
  }

  &:focus {
    outline: none;
    border-color: #C5337B;
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 0 20px rgba(197, 51, 123, 0.15);
    
    + ${InputIcon} {
      color: #C5337B;
    }
  }

  @media (max-width: 768px) {
    padding: 12px 12px 12px 45px;
    font-size: 0.9rem;
  }
`;

export const PasswordToggle = styled.button`
  position: absolute;
  right: 15px;
  background: none;
  border: none;
  color: rgba(43, 56, 87, 0.6);
  cursor: pointer;
  padding: 5px;
  border-radius: 5px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #C5337B;
    background: rgba(197, 51, 123, 0.1);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(197, 51, 123, 0.3);
  }
`;

// Error Message
export const ErrorMessage = styled.div`
  background: rgba(220, 38, 38, 0.2);
  border: 1px solid rgba(220, 38, 38, 0.5);
  color: #fca5a5;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.9rem;
  text-align: center;
  animation: ${fadeInUp} 0.3s ease-out;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 10px 14px;
  }
`;

// Submit Button
export const SubmitButton = styled.button`
  width: 100%;
  padding: 15px;
  background: linear-gradient(135deg, #C5337B 0%, #E91E63 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(197, 51, 123, 0.4);
    
    &:before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    
    &:hover {
      transform: none;
      box-shadow: none;
    }
  }

  @media (max-width: 768px) {
    padding: 12px;
    font-size: 1rem;
  }
`;

export const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

// Footer
export const Footer = styled.div`
  text-align: center;
  margin-top: 30px;
  color: rgba(43, 56, 87, 0.7);
  font-size: 0.9rem;
  animation: ${fadeInUp} 1s ease-out 0.6s both;

  @media (max-width: 768px) {
    margin-top: 20px;
    font-size: 0.8rem;
  }
`;
